/**
 * Retraits BALANCE (#57) — mirror de `net.nawam.wallet.dto.WithdrawalRequestView`
 * + DTOs de création / décision. Endpoints `/wallet/api/withdrawals/*` (couche
 * d'approbation opérateur/admin + seuils au-dessus de l'exécution GUTouch).
 *
 * Fichier **identique** entre les 3 apps (admin / owner / operator).
 */

export type WithdrawalStatus =
  | 'PENDING_OPERATOR'
  | 'PENDING_ADMIN'
  | 'APPROVED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'FAILED';

export type WithdrawalInitiator =
  | 'PRESTATAIRE_SELF'
  | 'OPERATOR_SELF'
  | 'OPERATOR_FOR_PRESTATAIRE'
  | 'ADMIN';

export interface WithdrawalRequestView {
  id: number;
  ownerType: string;
  ownerRef: number;
  amount: number;
  currency?: string | null;
  recipientNumber?: string | null;
  paymentMethodCode?: string | null;
  reason?: string | null;
  initiator?: WithdrawalInitiator | string | null;
  initiatorUserId?: number | null;
  operatorRef?: number | null;
  status: WithdrawalStatus;
  transferRef?: string | null;
  decidedByUserId?: number | null;
  decidedAt?: string | null;
  rejectReason?: string | null;
  createdAt?: string | null;
}

/** Body de `POST /wallet/api/withdrawals/me?ownerType=OWNER|OPERATOR`. */
export interface WithdrawRequest {
  amount: number;
  recipientNumber?: string | null;
  paymentMethodCode?: string | null;
  reason?: string | null;
}

/** Body de `POST /wallet/api/withdrawals/for-prestataire` (opérateur → prestataire géré). */
export interface CreateWithdrawalForPrestataireRequest {
  ownerType: string; // OWNER / DRIVER
  ownerRef: number;
  amount: number;
  recipientNumber?: string | null;
  paymentMethodCode?: string | null;
  reason?: string | null;
}

export const WITHDRAWAL_STATUS_LABEL: Record<WithdrawalStatus, string> = {
  PENDING_OPERATOR: 'status.withdrawal.PENDING_OPERATOR',
  PENDING_ADMIN: 'status.withdrawal.PENDING_ADMIN',
  APPROVED: 'status.withdrawal.APPROVED',
  REJECTED: 'status.withdrawal.REJECTED',
  COMPLETED: 'status.withdrawal.COMPLETED',
  FAILED: 'status.withdrawal.FAILED',
};

/** Classes Tailwind littérales (scannées par Tailwind) — rendu via interpolation. */
export const WITHDRAWAL_STATUS_BADGE: Record<WithdrawalStatus, string> = {
  PENDING_OPERATOR: 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
  PENDING_ADMIN: 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
  APPROVED: 'bg-blue-light-50 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400',
  REJECTED: 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400',
  COMPLETED: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400',
  FAILED: 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400',
};

/** En attente d'une décision (opérateur ou admin). */
export function isWithdrawalPending(s: WithdrawalStatus): boolean {
  return s === 'PENDING_OPERATOR' || s === 'PENDING_ADMIN';
}
